import React from "react";
import CodeBlock from "../components/CodeBlock";

const SseStreaming = () => (
  <section id="sse-streaming" className="developer-section">
    <h2>Working with /search and SSE Streaming</h2>
    <p>
      Plex uses Server-Sent Events (SSE) for real-time delivery of search
      results and LLM responses. This section covers the streaming protocol in
      detail.
    </p>

    <h3>Connection Lifecycle</h3>
    <p>
      Two ways to get here, depending on server version:
    </p>
    <ol className="dev-steps-list">
      <li>
        <strong>10.x, one call</strong> &mdash; POST directly to{" "}
        <code>/api/search/sse</code> or <code>/api/chat/sse</code>. The response
        <em> is</em> the SSE stream (or, if answered inline from cache, plain JSON
        with no stream at all).
      </li>
      <li>
        <strong>9.x, two calls</strong> (also works on 10.x) &mdash; Send a request
        with <code>stream: true</code>. The server responds immediately with a{" "}
        <code>stream_id</code>; open a separate connection to{" "}
        <code>/api/stream/&#123;stream_id&#125;</code> to receive events.{" "}
        <code>/api/stream/&#123;stream_id&#125;</code> requires auth (an{" "}
        <code>x-api-key</code> header or equivalent) — native <code>EventSource</code>{" "}
        can't send custom headers, so browser code needs a <code>fetch</code> +{" "}
        <code>ReadableStream</code> reader instead (see the full client example below).
      </li>
      <li>
        <strong>Receive</strong> &mdash; Process events as they arrive. Search
        events deliver progress and results; LLM events deliver streamed text.
      </li>
      <li>
        <strong>Close</strong> &mdash; The stream ends naturally after{" "}
        <code>search_results</code> (for search) or <code>message_stop</code>{" "}
        (for LLM). Close the connection on your end.
      </li>
    </ol>

    <h3>Search Event Types</h3>
    <table className="dev-api-params">
      <thead>
        <tr>
          <th>Event</th>
          <th>Data Format</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>search_update</code>
          </td>
          <td>
            <code>
              &#123;"progress": 45, "total": 100, "info": "Searching
              targets..."&#125;
            </code>
          </td>
          <td>
            Progress update during search execution. <code>progress</code> is
            0&ndash;100.
          </td>
        </tr>
        <tr>
          <td>
            <code>search_results</code>
          </td>
          <td>
            <code>
              &#123;"results": &#123;...&#125;, "totals": &#123;...&#125;,
              "page_token": "..."&#125;
            </code>
          </td>
          <td>
            Final search results. Same structure as the synchronous search
            response.
          </td>
        </tr>
        <tr>
          <td>
            <code>search_error</code>
          </td>
          <td>
            <code>&#123;"error": "Error message"&#125;</code>
          </td>
          <td>Search failed. Close the connection after receiving this.</td>
        </tr>
        <tr>
          <td>
            <code>ping</code>
          </td>
          <td>
            <em>(empty or timestamp)</em>
          </td>
          <td>
            Keep-alive signal sent periodically to prevent connection timeout.
          </td>
        </tr>
      </tbody>
    </table>

    <h3>LLM Streaming Event Types</h3>
    <p>
      Whether chat is requested via <code>POST /api/chat</code> or via{" "}
      <code>POST /api/search</code> with a <code>messages</code> body (still supported,
      not replaced by <code>/api/chat</code>), the stream delivers the AI response
      progressively using these events:
    </p>
    <table className="dev-api-params">
      <thead>
        <tr>
          <th>Event</th>
          <th>Data Format</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>message_start</code>
          </td>
          <td>
            <code>&#123;"role": "assistant"&#125;</code>
          </td>
          <td>New LLM response message begins.</td>
        </tr>
        <tr>
          <td>
            <code>content_block_start</code>
          </td>
          <td>
            <code>&#123;"type": "text"&#125;</code>
          </td>
          <td>
            A content block begins. Type is <code>text</code>,{" "}
            <code>tool_use</code>, or <code>tool_result</code>.
          </td>
        </tr>
        <tr>
          <td>
            <code>content_block_delta</code>
          </td>
          <td>
            <code>&#123;"delta": &#123;"text": "chunk..."&#125;&#125;</code>
          </td>
          <td>
            Incremental text content. Append <code>delta.text</code> to build
            the response.
          </td>
        </tr>
        <tr>
          <td>
            <code>content_block_stop</code>
          </td>
          <td>
            <code>&#123;&#125;</code>
          </td>
          <td>Current content block is complete.</td>
        </tr>
        <tr>
          <td>
            <code>message_delta</code>
          </td>
          <td>
            <code>&#123;"stop_reason": "end_turn"&#125;</code>
          </td>
          <td>
            Message metadata update. Includes stop reason when message ends.
          </td>
        </tr>
        <tr>
          <td>
            <code>message_stop</code>
          </td>
          <td>
            <code>&#123;"metrics": &#123;...&#125;&#125;</code>
          </td>
          <td>
            Message complete. Contains usage metrics. Close the connection.
          </td>
        </tr>
        <tr>
          <td>
            <code>message</code>
          </td>
          <td>
            <code>&#123;"role": "assistant", "content": [...]&#125;</code>
          </td>
          <td>Complete assembled message (sent after all deltas).</td>
        </tr>
        <tr>
          <td>
            <code>interrupt</code>
          </td>
          <td>
            <code>&#123;"reason": "..."&#125;</code>
          </td>
          <td>Stream was interrupted (e.g., agent canceled).</td>
        </tr>
        <tr>
          <td>
            <code>metadata</code>
          </td>
          <td>
            <code>&#123;"usage": &#123;...&#125;, "latency": ...&#125;</code>
          </td>
          <td>Session metadata including token usage and latency.</td>
        </tr>
        <tr>
          <td>
            <code>error</code>
          </td>
          <td>
            <code>&#123;"error": "message"&#125;</code>
          </td>
          <td>Error occurred during LLM processing.</td>
        </tr>
      </tbody>
    </table>

    <h3>Tool Use Events</h3>
    <p>
      When the LLM decides to call a tool (search, resolve, members), you will
      see this event sequence:
    </p>
    <pre className="dev-code-pre">
      <code>{`content_block_start  {"type": "tool_use", "tool_use": {"id": "tu_123", "name": "search"}}
content_block_delta  {"delta": {"tool_use": {"input": "{\\"ids\\": [\\"compound:...\\"]}" }}}
content_block_stop   {}
  ... server executes the tool and returns results ...
content_block_start  {"type": "text"}
content_block_delta  {"delta": {"text": "Based on the search results, ..."}}
...`}</code>
    </pre>

    <h3>Full Streaming Client Example</h3>
    <p>
      <code>/api/stream/&#123;stream_id&#125;</code> now requires auth, and{" "}
      <code>EventSource</code> cannot send an <code>x-api-key</code> header (or any
      custom header) — it can only connect with credentials baked into the URL, which
      this endpoint no longer accepts. The webapp itself dropped{" "}
      <code>EventSource</code> for this reason (see <code>ResumableEventSource</code>);
      the client below does the same, using <code>fetch</code> + a{" "}
      <code>ReadableStream</code> reader so the <code>x-api-key</code> header goes on
      every request, direct <code>/search/sse</code>/<code>/chat/sse</code> call or the
      9.x-style two-step <code>GET /stream/&#123;id&#125;</code>.
    </p>
    <CodeBlock
      title="Complete SSE client with reconnection"
      examples={{
        javascript: `class PlexStreamClient {
  constructor(baseUrl, apiKey) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
  }

  // Reads one SSE response body to completion, dispatching parsed events.
  // Works for both a direct /search/sse or /chat/sse response, and a
  // GET /stream/{id} response from the two-step flow — same wire format.
  async _readSSE(resp, onEvent) {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let lastActivity = Date.now();
    let result = null;

    const timeoutCheck = setInterval(() => {
      if (Date.now() - lastActivity > 300000) {
        reader.cancel();
        clearInterval(timeoutCheck);
        throw new Error("Stream timeout: no data received for 5 minutes");
      }
    }, 30000);

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lastActivity = Date.now();
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf("\\n\\n")) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          let type = "message";
          const dataLines = [];
          for (const line of block.split("\\n")) {
            if (!line || line.startsWith(":")) continue;
            const colon = line.indexOf(":");
            const field = colon === -1 ? line : line.slice(0, colon);
            let value_ = colon === -1 ? "" : line.slice(colon + 1);
            if (value_.startsWith(" ")) value_ = value_.slice(1);
            if (field === "event") type = value_;
            else if (field === "data") dataLines.push(value_);
          }
          if (dataLines.length === 0 && type === "message") continue;

          const data = dataLines.length ? JSON.parse(dataLines.join("\\n")) : {};
          onEvent?.(type, data);
          if (type === "search_results" || type === "message_stop") {
            result = data;
          }
          if (type === "search_error" || type === "error") {
            throw new Error(data.error || "Stream error");
          }
        }
      }
    } finally {
      clearInterval(timeoutCheck);
    }
    return result;
  }

  async search(params, onEvent) {
    // 10.x: one call, response is the stream (or inline JSON on a cache hit)
    const resp = await fetch(\`\${this.baseUrl}/api/search/sse\`, {
      method: "POST",
      headers: {
        "x-api-key": this.apiKey,
        "Content-Type": "application/json",
        Accept: "text/event-stream"
      },
      body: JSON.stringify(params)
    });

    if (resp.status === 404) {
      // 9.x: no /search/sse — stream=true, then GET /stream/{id} with the header
      const init = await fetch(\`\${this.baseUrl}/api/search?stream=true\`, {
        method: "POST",
        headers: { "x-api-key": this.apiKey, "Content-Type": "application/json" },
        body: JSON.stringify(params)
      }).then(r => r.json());

      const streamResp = await fetch(\`\${this.baseUrl}/api/stream/\${init.stream_id}\`, {
        headers: { "x-api-key": this.apiKey, Accept: "text/event-stream" }
      });
      return this._readSSE(streamResp, onEvent);
    }

    if (resp.headers.get("content-type")?.includes("application/json")) {
      return resp.json(); // answered inline, no stream to read
    }
    return this._readSSE(resp, onEvent);
  }
}

// Usage
const client = new PlexStreamClient("https://plexsearch.com", apiKey);
const results = await client.search(
  { ids: ["unichem:161671"] },
  (type, data) => console.log(\`[\${type}]\`, data)
);`,
        python: `import json
import requests
from sseclient import SSEClient  # use the bundled sseclient.py

API_URL = "https://plexsearch.com/api"
HEADERS = {"x-api-key": token, "content-type": "application/json"}

# One call: the response is the SSE stream (10.x)
sse_resp = requests.post(f"{API_URL}/search/sse",
                         headers={**HEADERS, "Accept": "text/event-stream"},
                         json={"ids": ["unichem:161671"]}, stream=True)
for event in SSEClient(sse_resp, events=["*"]).events():
    data = json.loads(event.data) if event.data else {}
    print(f"[{event.event}]", data)

    if event.event == "search_results":
        results = data
        break
    elif event.event in ("search_error", "error"):
        raise Exception(data.get("error", "Stream error"))`,
        curl: `curl -N "/api/search/sse" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/event-stream" \\
  -d '{"ids": ["unichem:161671"]}'

# Each SSE event appears as:
# event: search_update
# data: {"progress": 45, "total": 100, "info": "Searching..."}
#
# event: search_results
# data: {"results": {...}, "totals": {...}}`,
      }}
    />

    <h3>Choosing What to Consume</h3>
    <p>
      Depending on your use case you don't always need every raw event. These three
      patterns, built on <code>agent_results()</code> from{" "}
      <a href="/api_client.py" download>api_client.py</a>, show the range: full event
      stream, reconstituted messages only, or just the final result.
    </p>
    <CodeBlock
      title="Pattern 1: Full event stream (every delta, tool call, metadata)"
      examples={{
        python: `from api_client import agent_results

messages = [{"role": "user", "content": [{"text": "What targets does aspirin hit?"}]}]

for event in agent_results(messages, verbose=True):
    # Every event as the server sent it: message_start, content_block_start,
    # content_block_delta, content_block_stop, message_delta, tool_use, message_stop...
    print(f"[{event['type']}] {event['data']}")`,
      }}
    />
    <CodeBlock
      title="Pattern 2: Reconstituted messages only (skip the deltas)"
      examples={{
        python: `from api_client import agent_results

messages = [{"role": "user", "content": [{"text": "What targets does aspirin hit?"}]}]

for event in agent_results(messages):
    # "message" events carry the fully assembled message (text + tool_use blocks)
    # after all content_block_delta chunks for it have been merged server-side.
    if event["type"] == "message":
        messages.append(event["data"])
        print(event["data"])`,
      }}
    />
    <CodeBlock
      title="Pattern 3: Only the final result (ignore progress entirely)"
      examples={{
        python: `from api_client import agent_results

messages = [{"role": "user", "content": [{"text": "What targets does aspirin hit?"}]}]

final = None
for event in agent_results(messages):
    if event["type"] == "message_stop":
        final = event["data"]  # usage metrics; the last "message" event has the text

print(final)`,
      }}
    />

    <h3>Canceling a Stream</h3>
    <p>
      To cancel an in-progress agent or LLM operation, send DELETE to the stream
      with its stream ID, or with the conversation ID, which cancels that
      conversation's current turn. The turn stops together with every sub-agent
      it started. Pass <code>turn=STREAM_ID</code> to cancel only while that turn
      is still the current one (409 otherwise).
    </p>
    <CodeBlock
      title="Cancel a stream"
      examples={{
        curl: `curl -s -X DELETE "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN"`,
        python: `requests.delete(
    f"/api/stream/{stream_id}",
    headers={"x-api-key": token}
)`,
        javascript: `await fetch(
  \`/api/stream/\${streamId}\`,
  { method: "DELETE", headers: { "x-api-key": token } }
);`,
      }}
    />
  </section>
);

export default SseStreaming;
